import {
  Grid,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { useEffect, useState, useRef } from "react";
import { get } from "src/utils/networkUtils";

export default function GameTagSearch(props) {
  const { users, onUserSelect } = props;
  const prevUsersRef = useRef([]);
  const isInitialMount = useRef(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(users);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (isInitialMount.current) {
      setSelectedUsers(users || []);
      isInitialMount.current = false;
    }
  }, [users]);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    const getUsers = setTimeout(() => {
      get(`/admin/game-providers/tags?query=${searchQuery}`)
        .then((response) => {
          setSearchResults(response?.tags || []);
        })
        .finally(() => setLoading(false));
    }, 500);

    return () => clearTimeout(getUsers);
  }, [searchQuery]);

  useEffect(() => {
    const prevUsers = prevUsersRef.current;

    const prevJson = JSON.stringify(prevUsers);
    const currentJson = JSON.stringify(selectedUsers);

    if (prevJson !== currentJson) {
      onUserSelect(selectedUsers);
      prevUsersRef.current = selectedUsers;
    }
  }, [selectedUsers, onUserSelect]);

  useEffect(() => {
    if (!showResults) return;
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showResults]);

  const handleSelect = (user) => {
    setSelectedUsers((prevSelected) => {
      const isSelected = prevSelected.some(
        (selectedUser) =>
          selectedUser.tagId === user.tagId ||
          selectedUser.tagName === user.tagName
      );
      if (isSelected) {
        return prevSelected.filter(
          (selectedUser) =>
            selectedUser.tagId !== user.tagId &&
            selectedUser.tagName !== user.tagName
        );
      } else {
        return [...prevSelected, user];
      }
    });
  };
  const handleDelete = (user) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.filter(
        (selectedUser) =>
          selectedUser.tagId !== user.tagId &&
          selectedUser.tagName !== user.tagName
      )
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      const exists = selectedUsers.some(
        (user) =>
          user.tagName?.toLowerCase() === searchQuery.trim().toLowerCase()
      );

      if (!exists) {
        const newTag = {
          tagId: null,
          tagName: searchQuery.trim(),
        };

        setSelectedUsers((prev) => [...prev, newTag]);
      }

      setSearchQuery("");
      setSearchResults([]);
      setShowResults(false);
      e.preventDefault(); // stop form submission
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={3} sx={{ position: "relative" }} ref={searchRef}>
        <TextField
          margin="dense"
          id="kickoff"
          label="Search"
          type="text"
          fullWidth
          variant="standard"
          value={searchQuery}
          onFocus={() => setShowResults(true)}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowResults(true);
          }}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon={"eva:search-outline"} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Iconify
                  icon={"eva:close-outline"}
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                    setShowResults(false);
                  }}
                  sx={{ cursor: "pointer" }}
                />
              </InputAdornment>
            ),
          }}
          autoComplete="off"
        />
        {searchQuery && showResults && (
          <Paper
            elevation={2}
            sx={{
              position: "absolute",
              zIndex: 1300,
              width: "100%",
              maxHeight: 300,
              overflowY: "auto",
              top: "100%",
              left: 0,
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ m: 2 }} />
            ) : (
              <List dense>
                {searchResults.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No results found" />
                  </ListItem>
                ) : (
                  searchResults.map((user) => {
                    const isSelected = selectedUsers.some(
                      (selectedUser) => selectedUser.tagId === user.tagId
                    );
                    return (
                      <ListItemButton
                        key={user.tagId || user.userId}
                        onClick={() => handleSelect(user)}
                        selected={isSelected}
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={user.tagName}
                          secondary={user.tagId ? user.tagId : null}
                        />
                      </ListItemButton>
                    );
                  })
                )}
              </List>
            )}
          </Paper>
        )}
      </Grid>
      <Grid item xs={9}>
        {selectedUsers.length > 0 &&
          selectedUsers.map((user) => (
            <Chip
              label={user.username || user.tagName}
              variant="filled"
              onDelete={() => handleDelete(user)}
              key={user.id || user.tagName}
              sx={{ margin: 0.5 }}
            />
          ))}
      </Grid>
    </Grid>
  );
}
